// IndexHeroTV.js — versão TV/BROADCAST do IndexHeroBanner (RRTV)
// ============================================================
// O hero RICO de índice: banner com imagem + watermark da bandeira + logos
// FLUTUANTES dos componentes (top constituintes) + valor/variação AO VIVO do índice.
// TV-sized 1920×1080, determinístico (?index= ou ?symbol=). Roxo RR Magma.
//
// Rota (app.js):  <Route path="/tv/index-hero" element={<IndexHeroTV/>} />
// CONTROLES:
//   ?index=ibov|sp500|nasdaq|dax|nikkei|ftse   (default ibov)
// Dado vivo: /api/go82/api/indices → acha a linha do símbolo (last_quote, rate).
//   OBS: índices vivem no go82 (:8090), NÃO no /api/go (:8080, que é ticker de AÇÕES e
//   dá 404 pra ^BVSP). Mesma fonte do IndexTVPage/BoardTV. Tem dado inclusive fim de semana
//   (último fechamento em cache), igual aos outros componentes.
// heroImage: troque depois pelas nossas covers (intl_covers skylines/exchanges).

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';
const INDICES_URL = process.env.REACT_APP_GO82_URL || '/api/go82';   // índices/commodities/moedas (:8090)
const LOGO_HOST = '/api/logos';
const C = { purple: '#6660fe', green: '#34d399', red: '#f03e3e', white: '#e8e8f0' };
// Design system RR (fontes carregadas globalmente pelo site — NÃO usar @import externo: CSP bloqueia no deploy)
const F_HEAD = "'Roble', system-ui, sans-serif";           // headers/nomes/CTAs (800, -0.01em nos tight)
const F_BODY = "'Plus Jakarta Sans', system-ui, sans-serif"; // corpo/secundário
const F_MONO = "'IBM Plex Mono', monospace";               // labels/badges/tickers/tags

const INDICES = {
  ibov: { label: 'Ibovespa', symbol: '^BVSP', subtitle: 'Blue chips brasileiros — commodities, bancos, consumo',
    tag: 'IBOV · TOP 7', flag: 'br', accent: '#00d4a0',
    heroImage: 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'VALE3',name:'Vale'},{ticker:'PETR4',name:'Petrobras'},{ticker:'ITUB4',name:'Itaú'},
              {ticker:'BBDC4',name:'Bradesco'},{ticker:'ABEV3',name:'Ambev'},{ticker:'B3SA3',name:'B3'},{ticker:'WEGE3',name:'WEG'}] },
  sp500: { label: 'S&P 500', symbol: '^GSPC', subtitle: 'Mag 7 · big tech carrega o benchmark global',
    tag: 'S&P 500 · MAG 7', flag: 'us', accent: '#6660fe',
    heroImage: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'NVDA',name:'NVIDIA'},{ticker:'MSFT',name:'Microsoft'},{ticker:'AAPL',name:'Apple'},
              {ticker:'AMZN',name:'Amazon'},{ticker:'META',name:'Meta'},{ticker:'GOOGL',name:'Alphabet'},{ticker:'TSLA',name:'Tesla'}] },
  nasdaq: { label: 'Nasdaq 100', symbol: '^IXIC', subtitle: 'Top 7 · quase metade do índice em big tech',
    tag: 'NDX · TOP 7', flag: 'us', accent: '#22d3ee',
    heroImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'NVDA',name:'NVIDIA'},{ticker:'MSFT',name:'Microsoft'},{ticker:'AAPL',name:'Apple'},
              {ticker:'AMZN',name:'Amazon'},{ticker:'META',name:'Meta'},{ticker:'GOOGL',name:'Alphabet'},{ticker:'TSLA',name:'Tesla'}] },
  dax: { label: 'DAX', symbol: '^GDAXI', subtitle: 'Locomotivas alemãs — indústria, farma, seguros',
    tag: 'DAX · TOP 7', flag: 'de', accent: '#fbbf24',
    heroImage: 'https://images.unsplash.com/photo-1554072675-66db59dba46f?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'SAP',name:'SAP'},{ticker:'SIE.DE',name:'Siemens'},{ticker:'ALV.DE',name:'Allianz'},
              {ticker:'DTE.DE',name:'Deutsche Telekom'},{ticker:'AIR.PA',name:'Airbus'},{ticker:'MBG.DE',name:'Mercedes-Benz'}] },
  nikkei: { label: 'Nikkei 225', symbol: '^N225', subtitle: 'Blue chips japoneses — indústria, tech, financeiro',
    tag: 'NIKKEI · TOP 7', flag: 'jp', accent: '#f472b6',
    heroImage: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'7203.T',name:'Toyota'},{ticker:'6758.T',name:'Sony'},{ticker:'9432.T',name:'NTT'},
              {ticker:'8306.T',name:'Mitsubishi UFJ'},{ticker:'6861.T',name:'Keyence'},{ticker:'9984.T',name:'SoftBank'}] },
  ftse: { label: 'FTSE 100', symbol: '^FTSE', subtitle: 'Large caps de Londres — energia, farma, mineração',
    tag: 'FTSE · TOP 7', flag: 'gb', accent: '#a78bfa',
    heroImage: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1800&h=900&fit=crop&q=80',
    tickers: [{ticker:'AZN.L',name:'AstraZeneca'},{ticker:'SHEL.L',name:'Shell'},{ticker:'HSBA.L',name:'HSBC'},
              {ticker:'ULVR.L',name:'Unilever'},{ticker:'BP.L',name:'BP'},{ticker:'RIO.L',name:'Rio Tinto'}] },
};

const fmt = (n, d = 2) => n == null ? '—' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

const FloatingLogo = ({ ticker, name, size = 128 }) => {
  const [failed, setFailed] = useState(false);
  const base = (ticker || '').split('.')[0];
  const monogram = (name || ticker || '').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || 'RR';
  const shadow = 'drop-shadow(0 0 12px rgba(255,255,255,0.45)) drop-shadow(0 5px 12px rgba(0,0,0,0.7))';
  if (failed || !base) {
    return <div style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: F_HEAD, fontSize: size * 0.42, fontWeight: 800, letterSpacing: '-0.01em',
      color: 'rgba(255,255,255,0.92)', textShadow: '0 0 18px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.7)' }}>{monogram}</div>;
  }
  return <img src={`${LOGO_HOST}/${base}.svg`} alt={name} onError={() => setFailed(true)}
    style={{ width: size, height: size, objectFit: 'contain', filter: shadow, WebkitFilter: shadow }} />;
};

export default function IndexHeroTV() {
  const [params] = useSearchParams();
  const id = (params.get('index') || 'ibov').toLowerCase();
  const idx = INDICES[id] || INDICES.ibov;
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${INDICES_URL}/api/indices`);
        if (!r.ok || !alive) return;
        const list = await r.json();
        const row = (Array.isArray(list) ? list : []).find(x => x && x.symbol === idx.symbol);
        if (row && alive) setMeta({ ult: row.last_quote ?? row.close, var: row.rate });
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [idx.symbol]);

  const v = meta?.var;
  const up = (v ?? 0) >= 0;
  const vc = v == null ? C.gold : (up ? C.green : C.red);

  return (
    <div style={{ position: 'relative', width: 1920, height: 1080, overflow: 'hidden',
      background: `radial-gradient(1200px 700px at 78% 12%, ${idx.accent}22 0%, transparent 60%),`
        + ` radial-gradient(900px 600px at 12% 90%, ${C.purple}1f 0%, transparent 55%),`
        + ` linear-gradient(135deg, #0e0d1f 0%, #14132b 52%, #0a0a14 100%)`,
      fontFamily: F_BODY, color: C.white }}>

      {/* vinheta pra legibilidade */}
      <div style={{ position: 'absolute', inset: 0, background:
        'linear-gradient(180deg, rgba(7,7,10,0.35) 0%, rgba(7,7,10,0.00) 34%, rgba(7,7,10,0.30) 70%, rgba(7,7,10,0.85) 100%)' }} />

      {/* watermark self-contained: nome do índice gigante e apagado (sem imagem externa) */}
      <div style={{ position: 'absolute', right: -40, top: 60, opacity: 0.06, pointerEvents: 'none',
        fontFamily: F_HEAD, fontWeight: 800, fontSize: 340, lineHeight: 0.8, letterSpacing: '-0.03em',
        color: idx.accent, whiteSpace: 'nowrap', transform: 'rotate(-6deg)' }}>{idx.label}</div>

      {/* topo: RR MAGMA + tag */}
      <div style={{ position: 'absolute', top: 56, left: 64, right: 64, display: 'flex', alignItems: 'center', gap: 18 }}>
        <span style={{ fontFamily: F_MONO, fontSize: 26, fontWeight: 800, color: C.purple, background: 'rgba(102,96,254,0.20)',
          border: '1px solid rgba(102,96,254,0.5)', padding: '8px 20px', borderRadius: 6, letterSpacing: '0.18em',
          backdropFilter: 'blur(8px)' }}>RR MAGMA</span>
        <span style={{ fontFamily: F_MONO, fontSize: 22, fontWeight: 700, color: idx.accent, background: `${idx.accent}20`,
          border: `1px solid ${idx.accent}55`, padding: '7px 16px', borderRadius: 6, letterSpacing: '0.14em',
          backdropFilter: 'blur(8px)' }}>{idx.tag}</span>
      </div>

      {/* título + valor/variação AO VIVO */}
      <div style={{ position: 'absolute', top: 190, left: 64, right: 64, display: 'flex', alignItems: 'flex-end',
        justifyContent: 'space-between', gap: 40 }}>
        <div>
          <h2 style={{ fontFamily: F_HEAD, fontSize: 96, fontWeight: 800, margin: 0,
            lineHeight: 1.02, letterSpacing: '-0.02em', textShadow: '0 3px 20px rgba(0,0,0,0.75)' }}>{idx.label}</h2>
          <div style={{ fontFamily: F_BODY, fontWeight: 400, fontSize: 30,
            color: 'rgba(255,255,255,0.88)', marginTop: 14, maxWidth: 900, textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>{idx.subtitle}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: F_HEAD, fontSize: 92, fontWeight: 800, letterSpacing: '-0.01em', color: '#fff', lineHeight: 1, textShadow: '0 2px 16px rgba(0,0,0,0.8)' }}>
            {fmt(meta?.ult, meta?.ult >= 1000 ? 0 : 2)}
          </div>
          <div style={{ fontFamily: F_HEAD, fontSize: 60, fontWeight: 800, letterSpacing: '-0.01em', color: vc, marginTop: 10, textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
            {v == null ? '' : (up ? '▲ +' : '▼ ') + fmt(v) + '%'}
          </div>
        </div>
      </div>

      {/* logos flutuantes dos componentes */}
      <div style={{ position: 'absolute', bottom: 60, left: 64, right: 64 }}>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${idx.accent}00, ${idx.accent}66, ${idx.accent}00)`, marginBottom: 26 }} />
        <div style={{ fontFamily: F_MONO, fontSize: 20, letterSpacing: '0.16em', color: idx.accent, fontWeight: 700, marginBottom: 24,
          textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{idx.tickers.length} COMPONENTES</div>
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
          {idx.tickers.map(tk => (
            <div key={tk.ticker} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, minWidth: 128 }}>
              <FloatingLogo ticker={tk.ticker} name={tk.name} size={128} />
              <div style={{ fontFamily: F_MONO, fontSize: 18, fontWeight: 700, letterSpacing: '0.08em', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>{tk.ticker}</div>
              <div style={{ fontFamily: F_HEAD, fontWeight: 800, letterSpacing: '-0.01em', fontSize: 18,
                color: 'rgba(255,255,255,0.72)', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>{tk.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
